/**
 * useProviderRegistration
 *
 * TanStack Query mutation hook for provider registration
 *
 * API: POST /api/v1/credentialing/providers
 *
 * Request body:
 * - inviteCode: string
 * - companyName: string
 * - contactEmail: string
 * - contactPhone: string
 * - password: string
 * - primaryCounty: string
 * - additionalCounties?: string[]
 * - serviceTypes: string[]
 * - licenseNumber?: string
 * - insuranceInfo?: string
 * - certifications?: string
 *
 * Response:
 * - providerId: string
 * - status: 'pending'
 * - message: string
 * - authToken: string
 */

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import type { RegistrationFormData, RegistrationResponse } from '../types';

interface RegistrationResponseWithAuth extends RegistrationResponse {
  authToken: string;
}

export function useProviderRegistration() {
  return useMutation({
    mutationFn: async (data: RegistrationFormData): Promise<RegistrationResponseWithAuth> => {
      // Build service area array (primary + additional counties)
      const serviceArea = [data.primaryCounty];
      if (data.additionalCounties) {
        serviceArea.push(...data.additionalCounties);
      }

      // Build certifications array if provided
      const certifications = data.certifications
        ? data.certifications.split('\n').filter(cert => cert.trim())
        : undefined;

      const { data: result, error } = await apiClient.POST('/api/v1/credentialing/providers', {
        body: {
          name: data.companyName,
          email: data.contactEmail,
          serviceTypes: data.serviceTypes,
          serviceArea: serviceArea,
          licenseNumber: data.licenseNumber,
          certifications: certifications,
        },
      });

      if (error) {
        const errorMessage = error.error?.message || 'Registration failed';
        throw new Error(errorMessage);
      }

      // Note: The actual API response doesn't include authToken yet
      // This will need to be updated when the backend implements authentication
      return {
        ...(result as RegistrationResponse),
        authToken: 'temp-token', // Temporary until backend implements
      };
    },
    onSuccess: (data) => {
      // Store auth token
      localStorage.setItem('authToken', data.authToken);
      // Clear registration form data
      localStorage.removeItem('registrationFormData');
      // Redirect to onboarding status page
      window.location.href = '/onboarding';
    },
  });
}
